import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { PropertyService } from "../../../../services/property.service";
import { CityList, CountyList, StateList } from "../../../../models/RequestQuote";
import { forkJoin, map, Subscription } from "rxjs";
import { MatInputModule } from "@angular/material/input";
import { ActivatedRoute } from "@angular/router";
import { ToastService } from "../../../../services/toast.service";
import { SystemAdminService } from "../../../../services/system-admin.service";
import { CompanyType, CountryType, ToastType } from "../../../../enums/config.enum";
import { companyTypes, CountriesList } from "../../../../models/SystemAdmin.model";
import { MatSelectModule } from "@angular/material/select";
import { SpinnerComponent } from "../../../utils/spinner/spinner.component";
import { CountryISO, NgxIntlTelInputModule, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberUtil } from 'google-libphonenumber';

@Component({
    selector: 'app-company-details',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, MatInputModule, ReactiveFormsModule,  MatSelectModule, SpinnerComponent, NgxIntlTelInputModule],
    templateUrl: './company-details.component.html',
    styleUrl: './company-details.component.scss'
})
export class CompanyDetails implements OnInit, OnDestroy{
    @Input() companyDetailForm!: FormGroup;
    @Output() countryChange = new EventEmitter<number>();
    @Input() companyId!: string;
    @Input() formatPhoneNumber : (phoneNumber: string | null, countryCode?: CountryISO | null) => string | null = (phoneNumber: string | null) => null;
    @Input() extractRawPhoneNumber : (phoneNumber: string | null, countryCode?: CountryISO | null) => string | null = (phoneNumber: string | null) => null;
    countryId: number | null = null;
    companyTypeList = signal<{ label: string; value: number }[]>([]);
    statesList = signal<StateList[]>([]);
    countyList = signal<CountyList[]>([]);
    citiesList = signal<CityList[]>([]);
    countryList = signal<CountriesList[]>([]);
    searchState = signal<any>('');
    searchCounty = signal<any>('');
    searchCity = signal<any>('');
    filteredStatesList = signal<StateList[]>([]);
    filteredCountyList = signal<CountyList[]>([]);
    filteredCityList = signal<CityList[]>([]);
    activateRoute = inject(ActivatedRoute);
    propertyService = inject(PropertyService);
    systemAdminService = inject(SystemAdminService);
    toastService = inject(ToastService);
    companyTypeSubscription = new Subscription();
    stateSubscription = new Subscription();
    countySubscription = new Subscription();
    citySubscription = new Subscription();
    countrySubscription = new Subscription();
    countrySelectedValue = signal('');
    isLoading = signal<boolean>(false);
    CountryISO = CountryISO;
    SearchCountryField = SearchCountryField;
    phoneUtil = PhoneNumberUtil.getInstance();
    
    // Get all countries from PhoneNumberUtil for dropdown
    getAllCountries(): { code: CountryISO; name: string; displayName: string }[] {
      const countries: { code: CountryISO; name: string; displayName: string }[] = [];
      try {
        // Get all supported region codes
        const supportedRegions = this.phoneUtil.getSupportedRegions();
        for (const region of supportedRegions) {
          try {
            const countryCode = region as CountryISO;
            const countryCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
            const countryName = this.getCountryName(countryCode);
            const displayName = countryCallingCode ? `+${countryCallingCode} ${countryName}` : countryName;
            countries.push({ code: countryCode, name: countryName, displayName });
          } catch {
            // Skip invalid regions
          }
        }
      } catch {
        // Fallback to common countries if getting all regions fails
        countries.push(
          { code: CountryISO.UnitedStates, name: 'United States', displayName: '+1 United States' },
          { code: CountryISO.UnitedKingdom, name: 'United Kingdom', displayName: '+44 United Kingdom' }
        );
      }
      return countries.sort((a, b) => a.name.localeCompare(b.name));
    }
    
    getCountryName(countryCode: CountryISO): string {
      const codeStr = String(countryCode).toUpperCase();
      
      // Primary: Use browser's Intl.DisplayNames API (standard, supports all countries)
      try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const countryName = displayNames.of(codeStr);
        if (countryName) {
          return countryName;
        }
      } catch (error) {
        // Intl.DisplayNames not supported, fall through to fallback
      }
      
      // Fallback: Only use hardcoded mapping if Intl API is not available
      const countryNames: { [key: string]: string } = {
        'US': 'United States', 'GB': 'United Kingdom', 'CA': 'Canada', 'AU': 'Australia',
        'FR': 'France', 'DE': 'Germany', 'IT': 'Italy', 'ES': 'Spain', 'NL': 'Netherlands',
        'BE': 'Belgium', 'CH': 'Switzerland', 'AT': 'Austria', 'SE': 'Sweden', 'NO': 'Norway',
        'DK': 'Denmark', 'FI': 'Finland', 'PL': 'Poland', 'CZ': 'Czech Republic', 'IE': 'Ireland',
        'PT': 'Portugal', 'GR': 'Greece', 'LU': 'Luxembourg', 'IS': 'Iceland', 'MT': 'Malta',
        'CY': 'Cyprus', 'EE': 'Estonia', 'LV': 'Latvia', 'LT': 'Lithuania', 'SI': 'Slovenia',
        'SK': 'Slovakia', 'HU': 'Hungary', 'RO': 'Romania', 'BG': 'Bulgaria', 'HR': 'Croatia',
        'JP': 'Japan', 'CN': 'China', 'IN': 'India', 'KR': 'South Korea', 'SG': 'Singapore',
        'MY': 'Malaysia', 'TH': 'Thailand', 'PH': 'Philippines', 'ID': 'Indonesia', 'VN': 'Vietnam',
        'NZ': 'New Zealand', 'ZA': 'South Africa', 'BR': 'Brazil', 'MX': 'Mexico', 'AR': 'Argentina',
        'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru', 'AE': 'United Arab Emirates', 'SA': 'Saudi Arabia',
        'IL': 'Israel', 'TR': 'Turkey', 'RU': 'Russia', 'UA': 'Ukraine', 'EG': 'Egypt',
        'NG': 'Nigeria', 'KE': 'Kenya', 'GH': 'Ghana', 'MA': 'Morocco', 'TN': 'Tunisia'
      };
      
      if (countryNames[codeStr]) {
        return countryNames[codeStr];
      }
      
      // Final fallback: format the code nicely
      return codeStr.split('').map((char, i) => i === 0 ? char.toUpperCase() : char.toLowerCase()).join('');
    }

    getMaxPhoneLength(countryCode: CountryISO | null | undefined): number {
      if (!countryCode) return 15;
      const regionCode = String(countryCode).toLowerCase();
      
      // Comprehensive country-to-max-length mapping
      const countryMaxLengths: { [key: string]: number } = {
        // Very short numbers (4-5 digits)
        'nu': 4,  // Niue
        'tk': 4,  // Tokelau
        'nf': 5,  // Norfolk Island
        
        // Short numbers (6-7 digits)
        'gi': 8,  // Gibraltar
        'va': 8,  // Vatican City
        'sm': 9,  // San Marino
        'mc': 9,  // Monaco
        'li': 9,  // Liechtenstein
        
        // Standard 10-digit numbers (US, Canada, etc.)
        'us': 10, // United States
        'ca': 10, // Canada
        'ru': 10, // Russia
        'kz': 10, // Kazakhstan
        'mx': 10, // Mexico
        'br': 11, // Brazil (can be 10 or 11)
        'in': 10, // India
        'au': 9,  // Australia
        'nz': 9,  // New Zealand
        'za': 9,  // South Africa
        'ar': 10, // Argentina
        'cl': 9,  // Chile
        'co': 10, // Colombia
        'pe': 9,  // Peru
        've': 10, // Venezuela
        'ec': 9,  // Ecuador
        'uy': 8,  // Uruguay
        'py': 9,  // Paraguay
        'bo': 8,  // Bolivia
        'cr': 8,  // Costa Rica
        'pa': 8,  // Panama
        'gt': 8,  // Guatemala
        'hn': 8,  // Honduras
        'ni': 8,  // Nicaragua
        'sv': 8,  // El Salvador
        'do': 10, // Dominican Republic
        'cu': 8,  // Cuba
        'jm': 10, // Jamaica
        'tt': 10, // Trinidad and Tobago
        'bb': 10, // Barbados
        'bs': 10, // Bahamas
        'bz': 7,  // Belize
        'gy': 7,  // Guyana
        'sr': 7,  // Suriname
        'gf': 9,  // French Guiana
        
        // 11-digit numbers (UK, etc.)
        'gb': 11, // United Kingdom
        'ie': 9,  // Ireland
        'nl': 9,  // Netherlands
        'be': 9,  // Belgium
        'lu': 9,  // Luxembourg
        'ch': 9,  // Switzerland
        'at': 10, // Austria
        'de': 11, // Germany
        'dk': 8,  // Denmark
        'se': 9,  // Sweden
        'no': 8,  // Norway
        'fi': 9,  // Finland
        'is': 7,  // Iceland
        'pl': 9,  // Poland
        'cz': 9,  // Czech Republic
        'sk': 9,  // Slovakia
        'hu': 9,  // Hungary
        'ro': 10, // Romania
        'bg': 9,  // Bulgaria
        'gr': 10, // Greece
        'pt': 9,  // Portugal
        'es': 9,  // Spain
        'it': 10, // Italy
        'mt': 8,  // Malta
        'cy': 8,  // Cyprus
        
        // Eastern Europe
        'ua': 9,  // Ukraine
        'by': 9,  // Belarus
        'lt': 8,  // Lithuania
        'lv': 8,  // Latvia
        'ee': 8,  // Estonia
        'md': 8,  // Moldova
        'rs': 9,  // Serbia
        'hr': 9,  // Croatia
        'si': 8,  // Slovenia
        'ba': 8,  // Bosnia and Herzegovina
        'mk': 9,  // North Macedonia
        'al': 9,  // Albania
        'me': 8,  // Montenegro
        'xk': 8,  // Kosovo
        
        // Middle East
        'sa': 9,  // Saudi Arabia
        'ae': 9,  // UAE
        'kw': 8,  // Kuwait
        'qa': 8,  // Qatar
        'bh': 8,  // Bahrain
        'om': 8,  // Oman
        'ye': 9,  // Yemen
        'iq': 10, // Iraq
        'ir': 10, // Iran
        'il': 9,  // Israel
        'jo': 9,  // Jordan
        'lb': 8,  // Lebanon
        'sy': 9,  // Syria
        'ps': 9,  // Palestine
        'tr': 10, // Turkey
        'eg': 10, // Egypt
        'ly': 9,  // Libya
        'tn': 8,  // Tunisia
        'dz': 9,  // Algeria
        'ma': 9,  // Morocco
        'sd': 9,  // Sudan
        'et': 9,  // Ethiopia
        
        // Asia
        'cn': 11, // China
        'jp': 10, // Japan
        'kr': 10, // South Korea
        'tw': 9,  // Taiwan
        'hk': 8,  // Hong Kong
        'mo': 8,  // Macau
        'sg': 8,  // Singapore
        'my': 9,  // Malaysia
        'th': 9,  // Thailand
        'vn': 10, // Vietnam
        'ph': 10, // Philippines
        'id': 9,  // Indonesia
        'mm': 9,  // Myanmar
        'kh': 9,  // Cambodia
        'la': 8,  // Laos
        'bn': 7,  // Brunei
        'bd': 10, // Bangladesh
        'pk': 10, // Pakistan
        'lk': 9,  // Sri Lanka
        'np': 10, // Nepal
        'af': 7,  // Afghanistan
        'uz': 9,  // Uzbekistan
        'tm': 8,  // Turkmenistan
        'tj': 9,  // Tajikistan
        'kg': 9,  // Kyrgyzstan
        'mn': 8,  // Mongolia
        
        // Oceania
        'pg': 8,  // Papua New Guinea
        'fj': 7,  // Fiji
        'nc': 6,  // New Caledonia
        'pf': 6,  // French Polynesia
        'ws': 7,  // Samoa
        'to': 7,  // Tonga
        'vu': 7,  // Vanuatu
        'sb': 7,  // Solomon Islands
        'ki': 5,  // Kiribati
        'tv': 6,  // Tuvalu
        'nr': 7,  // Nauru
        'pw': 7,  // Palau
        'fm': 7,  // Micronesia
        'mh': 7,  // Marshall Islands
        
        // Africa
        'ng': 10, // Nigeria
        'ke': 9,  // Kenya
        'tz': 9,  // Tanzania
        'ug': 9,  // Uganda
        'rw': 9,  // Rwanda
        'gh': 9,  // Ghana
        'ci': 8,  // Côte d'Ivoire
        'sn': 9,  // Senegal
        'ml': 8,  // Mali
        'bf': 8,  // Burkina Faso
        'ne': 8,  // Niger
        'td': 8,  // Chad
        'cm': 9,  // Cameroon
        'ga': 8,  // Gabon
        'cg': 9,  // Republic of the Congo
        'cd': 9,  // DR Congo
        'ao': 9,  // Angola
        'zm': 9,  // Zambia
        'zw': 9,  // Zimbabwe
        'bw': 8,  // Botswana
        'na': 9,  // Namibia
        'mw': 9,  // Malawi
        'mz': 9,  // Mozambique
        'mg': 9,  // Madagascar
        'mu': 8,  // Mauritius
        'sc': 7,  // Seychelles
        'km': 7,  // Comoros
        'dj': 8,  // Djibouti
        'er': 7,  // Eritrea
        'so': 8,  // Somalia
        'ss': 9,  // South Sudan
        
        // Caribbean
        'ht': 8,  // Haiti
        'pr': 10, // Puerto Rico
        'vi': 10, // US Virgin Islands
        'vg': 10, // British Virgin Islands
        'ky': 7,  // Cayman Islands
        'bm': 10, // Bermuda
        'tc': 7,  // Turks and Caicos
        'ai': 7,  // Anguilla
        'ms': 7,  // Montserrat
        'gd': 7,  // Grenada
        'lc': 7,  // Saint Lucia
        'vc': 7,  // Saint Vincent
        'ag': 10,  // Antigua and Barbuda
        'dm': 7,  // Dominica
        'kn': 7,  // Saint Kitts and Nevis
        
        // Other territories
        'fo': 6,  // Faroe Islands
        'gl': 6,  // Greenland
        'sj': 8,  // Svalbard
        'ax': 6,  // Åland Islands
        'gg': 10, // Guernsey
        'je': 10, // Jersey
        'im': 10, // Isle of Man
        'ad': 6,  // Andorra
      };
      
      return countryMaxLengths[regionCode] || 15; // Default to 15 for unknown countries
    }

    ngOnInit(): void {
        this.loadCompanyTypes();
        this.getCountryList();
        if(this.companyDetailForm.get('companyType')?.value && this.companyDetailForm.get('countryId')?.value && this.companyDetailForm.get('stateId')?.value){
            if(String(this.companyDetailForm.get('companyType')?.value) === String(CompanyType.BROKER_COMPANY)){
                // this.companyDetailForm.get('countryId')?.disable();
            }
            this.getStateCountyAndCitiesList(this.companyDetailForm.get('countryId')?.value, this.companyDetailForm.get('stateId')?.value)
        }

        // Manually check for null value after the form is initialized
        // this.companyTypeSubscription = this.companyDetailForm.get('companyType')?.valueChanges.subscribe(value => {
        //     if (value === "null") {
        //         this.companyDetailForm.get('companyType')?.setErrors({ required: true });
        //         this.companyDetailForm.get('stateId')?.setValue(null);
        //     } else {
        //         // Clear error if a valid option is selected
        //         this.companyDetailForm.get('companyType')?.setErrors(null);
        //         this.companyDetailForm.get('stateId')?.setValue(null);
        //     }
        // }) || new Subscription();

        this.stateSubscription = this.companyDetailForm.get('stateId')?.valueChanges.subscribe(value => {
            // this.companyDetailForm.patchValue({countyId: null, cityId: null});
            if (value === null || value === "null") {
                this.companyDetailForm.get('stateId')?.setErrors({ required: true });
            }else{
                // Clear error if a valid option is selected
                this.companyDetailForm.get('stateId')?.setErrors(null);
                if(value){
                    this.getCountyAndCitiesList(Number(value));
                }
            }
        }) || new Subscription();

        this.countySubscription = this.companyDetailForm.get('countyId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.companyDetailForm.get('countyId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.companyDetailForm.get('countyId')?.setErrors(null);
            }
        }) || new Subscription();

        this.citySubscription = this.companyDetailForm.get('cityId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.companyDetailForm.get('cityId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.companyDetailForm.get('cityId')?.setErrors(null);
            }
        }) || new Subscription();

        this.countrySubscription = this.companyDetailForm.get('countryId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.companyDetailForm.get('countryId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.companyDetailForm.get('countryId')?.setErrors(null);
                if(value && String(value) !== String(this.countrySelectedValue())){
                   this.countrySelectedValue.set(value);
                   this.getStatesByCountryId(Number(value)) // based on the country list selection pass id
                }
            }
        }) || new Subscription();

        // Check if countryId is already set and fetch states for it (handles binding and tab navigation)
        const existingCountryId = this.companyDetailForm.get('countryId')?.value;
        if (existingCountryId && existingCountryId !== null && existingCountryId !== "null") {
            this.countrySelectedValue.set(existingCountryId);
            this.getStatesByCountryId(Number(existingCountryId));
        }
    }

    onPhoneNumberInput(event: any) {
        const countryCode = this.companyDetailForm.get('corporatePhoneCountryCode')?.value;
        const formattedNumber = this.formatPhoneNumber(event.target.value, countryCode);
        this.companyDetailForm.get('corporatePhone')?.setValue(formattedNumber);
        this.companyDetailForm.get('corporatePhone')?.updateValueAndValidity();
    }
    
    onCorporatePhoneCountryChange(country: any) {
        if (country && country.iso2) {
            this.companyDetailForm.get('corporatePhoneCountryCode')?.setValue(country.iso2);
            // Reset phone number field when country changes
            this.companyDetailForm.get('corporatePhone')?.setValue('', { emitEvent: false });
        }
    }
    
    // Convert calling code string (e.g., "+93") to CountryISO
    mapCallingCodeToISO(callingCode: string | number | null): CountryISO | null {
      if (!callingCode) {
        return null;
      }
      
      const codeStr = String(callingCode).replace(/^\+/, ''); // Remove + if present
      const codeNum = parseInt(codeStr, 10);
      
      if (isNaN(codeNum)) return null;
      
      try {
        // Iterate through all supported regions to find matching calling code
        const supportedRegions = this.phoneUtil.getSupportedRegions();
        for (const region of supportedRegions) {
          try {
            const regionCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
            if (regionCallingCode === codeNum) {
              // Convert to uppercase to match CountryISO enum values
              return region.toUpperCase() as CountryISO;
            }
          } catch {
            // Skip invalid regions
          }
        }
      } catch {
        // Fallback to common mappings
      }
      
      // Fallback mappings for common countries
      if (codeNum === 1) return CountryISO.UnitedStates;
      if (codeNum === 44) return CountryISO.UnitedKingdom;
      if (codeNum === 93) return CountryISO.Afghanistan;
      
      return null;
    }    

      formatUSTelephone(value: string): string {
        // Format US phone number
        
        if (!value) return '';
        const cleaned = ('' + value).replace(/\D/g, '');
        const match = RegExp(/^(\d{3})(\d{3})(\d{4})$/).exec(cleaned);
        return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value;
      }
    
      formatUKTelephone(value: string): string {
        // Format UK phone number
        if (!value) return '';
        const cleaned = ('' + value).replace(/\D/g, '');
        const match = RegExp(/^(\d{2})(\d{4})(\d{4})$/).exec(cleaned);
        return match ? `${match[1]} ${match[2]} ${match[3]}` : value;
      }

    fetchCompanyDetails(companyId: string) {
        this.systemAdminService.getCompanyDetails(companyId).subscribe({
          next:(response: any) => {
            if(response){
              this.onBindFormData(response);
            }
          },
          error: (error: any) => {
            console.log("error", error);
            // show error notification
            this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
          }
        })
    }

 
    
    onBindFormData(formValue: any) {
        
        this.companyDetailForm.patchValue({
          companyType: formValue?.companyType,
          corporateMail: formValue?.corporateMail,
          companyName: formValue?.companyName,
          unitNum: formValue?.address?.unitNum,
          streetNum: formValue?.address?.streetNum,
          streetName: formValue?.address?.streetName,
          cityId: formValue?.address?.cityId,
          countyId: formValue?.address?.countyId,
          stateId: formValue?.address?.stateId,
          zipCode: formValue?.address?.zipCode,
          corporatePhone: this.formatUSTelephone(formValue?.corporatePhone),
          websiteURL: formValue?.websiteURL,
          contactPersonName: formValue?.contactPersonName,
          contactPersonRole: formValue?.contactPersonRole,
          contactPersonEmail: formValue?.contactPersonEmail,
          contactPersonPhone: this.formatUSTelephone(formValue?.contactPersonPhone),
          countryId: formValue?.address?.countryId,
          isActive: formValue?.isActive,
          isDeleted: formValue?.isDeleted
        });

        // Fetch states for the bound countryId if it exists
        const countryId = formValue?.address?.countryId;
        if (countryId && countryId !== null && countryId !== "null") {
            this.countrySelectedValue.set(countryId);
            // If stateId exists, fetch state, county, and cities; otherwise just fetch states
            if (formValue?.address?.stateId) {
                this.getStateCountyAndCitiesList(Number(countryId), Number(formValue.address.stateId));
            } else {
                this.getStatesByCountryId(Number(countryId));
            }
        }
    }

    allowOnlyAlphanumeric(event: KeyboardEvent): void {
        const key = event.key;
        
        // Check if the key is not an alphanumeric character (0-9, a-z, A-Z)
        if (!/^[a-zA-Z0-9 ]$/.test(key) && key !== "Backspace" && key !== "Delete" && key !== "ArrowLeft" && key !== "ArrowRight") {
            event.preventDefault(); // Prevent non-alphanumeric input
        }
    }

    getStatesList() {
        this.propertyService.getStates().subscribe({
            next: (response:any) => {
                if(response && response?.length > 0){
                    this.statesList.set(response);
                }
            },
            error: (error: any) => {
                console.log("error", error);
            } 
        })
    }

    getStatesByCountryId(countryId: number) {
        this.isLoading.set(true);
        this.systemAdminService.getStateByCountryId(countryId).subscribe({
            next: (response:any) => {
                if(response && response?.length > 0){
                    this.statesList.set(response);
                    this.filteredStatesList.set(response);
                    this.isLoading.set(false);
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.isLoading.set(false);
            } 
        })
    }

    loadCompanyTypes(): void {
        this.systemAdminService.getAllCompanyTypes().subscribe({
          next: (response: companyTypes) => {
            if (Array.isArray(response)) {
              const formattedData = response.map((item) => ({
                label: item.code,     
                value: item.gencodeId  
              }));
              this.companyTypeList.set(formattedData); 
            } else {
              console.error('Unexpected response format:', response);
            }
          },
          error: (error: any) => {
            console.error('Error fetching company types:', error);
          }
        });
    }
      

    onStateChange() {
        this.companyDetailForm.patchValue({countyId: null, cityId: null});
    }
    
    onCompanyTypeChange(event: Event) {
        const selectedValue = event.target as HTMLSelectElement;
        const countryField = this.companyDetailForm.get('countryId');
        if(selectedValue.value && selectedValue.value === String(CompanyType.BROKER_COMPANY)){
            // countryField?.disable();
            countryField?.setValue(CountryType.US);
            this.companyDetailForm.patchValue({stateId: null});
            this.onStateChange() // reset State dropdown
            this.getStatesByCountryId(CountryType.US);
        }else{
            countryField?.enable();
            this.companyDetailForm.patchValue({countryId: null});
            this.onStateChange() // reset State dropdown
        }
    }

    getCountryList() {
        this.systemAdminService.getCountryList().subscribe({
            next: (response: any) => {
                if(response){
                    this.countryList.set(response);
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
            }
        })
    }

    onCountryChange(event: any){
        this.companyDetailForm.patchValue({zipCode:null, stateId:null, countyId: null, cityId: null});
        const selectedCountryId = event.target.value;   
        this.countryId=selectedCountryId
             
        this.countryChange.emit(selectedCountryId); // Emit the selected country ID
        // this.formatPhoneNumber(event)
    }

    getStateName(stateId: string | null): string {
        if (!stateId) return '';
        const state =Array.from(this.statesList().values()).flat().find(s => String(s.stateId) === String(stateId));
        return state ? state.stateName : '';
    }

    getCountyName(countyId: string | null): string {
        if (!countyId) return '';
        const county = this.countyList().find(c => String(c.countyId) === String(countyId));
        return county ? county.countyName : '';
    }

    getCityName(cityId: string | null): string {
        if (!cityId) return '';
        const city = this.citiesList().find(c => String(c.cityId) === String(cityId));
        return city ? city.cityName : '';
    }

    onStateDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchState.set('');
            this.filteredStatesList.set([...this.statesList()]);
        }
    }

    onCountyDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchCounty.set('');
            this.filteredCountyList.set([...this.countyList()]);
        }
    }

    onCityDropdownOpened(isOpen: boolean){
        if (!isOpen) {
            this.searchCity.set('');
            this.filteredCityList.set([...this.citiesList()]);
        }
    }

    onStateSearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailState", 1200, () => {
          this.updateStateSearchTerm(value);
        });
    }

    onCountySearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailCounty", 1200, () => {
          this.updateCountySearchTerm(value);
        });
    }

    onCitySearch(event: Event) {
        const value = (event.target as HTMLInputElement).value; 
    
        this.propertyService.applyDelayedFilter("CompanyDetailCity", 1200, () => {
          this.updateCitySearchTerm(value);
        });
    }
      
    updateStateSearchTerm(value: string) {
        this.searchState.set(value);
        const filtered = this.statesList().filter((state) =>
            state.stateName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredStatesList.set(filtered);
    }

    updateCountySearchTerm(value: string) {
        this.searchCounty.set(value);
        const filtered = this.countyList().filter((county) =>
          county.countyName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredCountyList.set(filtered);
    }

    updateCitySearchTerm(value: string) {
        this.searchCity.set(value);
        const filtered = this.citiesList().filter((city) =>
          city.cityName.toLowerCase().includes(value.toLowerCase())
        );
        this.filteredCityList.set(filtered);
    }

    getStateCountyAndCitiesList(countryId: number, stateId: number) {
        this.isLoading.set(true); 
        forkJoin([
            this.systemAdminService.getStateByCountryId(countryId),
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId)
        ]).pipe(
            map(([state, counties, cities]) => {
              return {state, counties, cities};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    const countiesSortedList = this.propertyService.sortCountyRecords(response.counties);
                    const citiesSortedList = this.propertyService.sortCityRecords(response.cities);
                    this.statesList.set(response?.state)
                    this.countyList.set(countiesSortedList);
                    this.citiesList.set(citiesSortedList);
                    this.filteredStatesList.set(response?.state);
                    this.filteredCountyList.set(countiesSortedList);
                    this.filteredCityList.set(citiesSortedList);
                    //const getCountryFromSelectedState = this.statesList().filter((state) => state.stateId === stateId);
                    //setting the country Id from selected State
                    //getCountryFromSelectedState.length > 0 && this.companyDetailForm.patchValue({countryId: getCountryFromSelectedState[0].countryId})
                    this.isLoading.set(false); 
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.isLoading.set(false); 
            }
        })
    }

    getCountyAndCitiesList(stateId: number) {   
        this.isLoading.set(true);     
        forkJoin([
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId)
        ]).pipe(
            map(([counties, cities]) => {
              return {counties, cities};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    const countiesSortedList = this.propertyService.sortCountyRecords(response.counties);
                    const citiesSortedList = this.propertyService.sortCityRecords(response.cities);
                    this.countyList.set(countiesSortedList);
                    this.citiesList.set(citiesSortedList);
                    this.filteredCountyList.set(countiesSortedList);
                    this.filteredCityList.set(citiesSortedList);
                    //const getCountryFromSelectedState = this.statesList().filter((state) => state.stateId === stateId);
                    //setting the country Id from selected State
                    //getCountryFromSelectedState.length > 0 && this.companyDetailForm.patchValue({countryId: getCountryFromSelectedState[0].countryId})
                    this.isLoading.set(false);
                }
            },
            error: (error: any) => {
                console.log("error", error);
                this.isLoading.set(false);
            }
        })
    }

      onZipCodeBlur() {
        
        const zipcode = this.companyDetailForm.get('zipCode')?.value;
        const countryId = this.companyDetailForm.get('countryId')?.value;
        
        // Only fetch state by zip code if country is US (1)
        // Exit early if zipcode is missing
        if (!zipcode) {
            return;
        }
        
        // Exit early if countryId is missing, null, undefined, or empty string
        if (countryId === null || countryId === undefined || countryId === '') {
            return;
        }
        
        // Convert to number and validate
        const countryIdNum = Number(countryId);
        if (isNaN(countryIdNum)) {
            return; // Invalid countryId, exit
        }
        
        // Only proceed if countryId equals US (1) - explicitly check for 1
        if (countryIdNum === CountryType.US) {
            this.fetchStateByZipCode(zipcode);
        }
        // If countryId is UK (2) or any other country, do nothing
    }

      fetchStateByZipCode( zipcode: string) {
        const countryId = this.companyDetailForm.get('countryId')?.value;
        this.propertyService.getZipCode(zipcode, countryId).subscribe({
          next: (response: any) => {
            if(response && response?.State?.length > 0){
              //this.updateStateList(index, response?.State); // update State List
              this.fetchCountyAndCity(response?.State[0], response?.State, response?.County, response?.City);
              
              this.companyDetailForm.patchValue({cityId: response?.City[0], countyId: response?.County[0], stateId: response?.State[0]});  
              
            }else{
              this.toastService.addNotification({message: "Invalid Zip Code, please enter a valid Zip Code", type: ToastType.Error}); // error notification
              this.companyDetailForm.patchValue({cityId: '', countyId: '', stateId: ''});
              this.statesList.set([]); // reset the list for invalid record
              this.countyList.set([]); // reset the list for invalid record
              this.citiesList.set([]); // reset the list for invalid record
              this.filteredStatesList.set([]); // reset the list for invalid record
              this.filteredCountyList.set([]); // reset the list for invalid record
              this.filteredCityList.set([]); // reset the list for invalid record
            }
          },
          error: (error: any) => {
            this.companyDetailForm.patchValue({city: '', country: '', state: ''});
            this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
          }
        })
      }

        fetchCountyAndCity(stateId: number, stateList: any[], countiesList: any[] = [], citiesList: any[] = []){
          this.isLoading.set(true);
          forkJoin([
            this.propertyService.getStates(),
            this.propertyService.getCounties(stateId),
            this.propertyService.getCities(stateId),
          ]).pipe(
            map(([statesList, countyList, cityList]) => { return {statesList, countyList, cityList}})
          ).subscribe({
            next: (response: any) => {
                if(response){
                  this.isLoading.set(false);
                  this.updateStateList( response.statesList, stateList);
                  
                  const countiesSortedList = this.propertyService.sortCountyRecords(response.countyList);
                  const citiesSortedList = this.propertyService.sortCityRecords(response.cityList);
                  if(countiesList?.length > 0 && citiesList?.length > 0){
                    const filteredCounties = countiesSortedList.filter(countyId => countiesList.includes(countyId.countyId));

                    const filteredCities = citiesSortedList.filter(cityId => citiesList.includes(cityId.cityId));

                    this.updateCountyAndCityList(filteredCounties, filteredCities);
                  }else{
                    this.updateCountyAndCityList(countiesSortedList, citiesSortedList);
                  }
                }
            },
            error: (error: any) => {
                this.isLoading.set(false);
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
          })
        }

    updateStateList(stateList: any[], stateIDList: any[]) {
            const filteredStates = stateList.filter(state => stateIDList.includes(state.stateId));
            this.statesList.set(filteredStates);
            this.filteredStatesList.set(filteredStates);
        }

        updateCountyAndCityList(countiesList: any[] = [], citiesList: any[] = []) {
            this.countyList.set(countiesList);
            this.citiesList.set(citiesList);
            this.filteredCountyList.set(countiesList);
            
            this.filteredCityList.set(citiesList);
            // this.isCityStateListFetched.set(true);
        }
      
     allowStreetNumberCharacters(event: KeyboardEvent): void {
      const allowedPattern = /^[0-9a-zA-Z\-\/ .#]$/;  
      const key = event.key;

      // allow control keys like Backspace, Delete, Tab, Arrow keys
      if (
        key === 'Backspace' ||
        key === 'Delete' ||
        key === 'Tab' ||
        key === 'ArrowLeft' ||
        key === 'ArrowRight'
      ) {
        return;
      }

      if (!allowedPattern.test(key)) {
        event.preventDefault();
      }
    }

    ngOnDestroy(): void {
        this.companyTypeSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.countySubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.countrySubscription.unsubscribe();
    }
}